import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

type InquiryOption = {
  key: string;
  label: string;
};

type FormState = {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
};

const inquiryOptions: InquiryOption[] = [
  { key: 'system', label: 'システム開発について' },
  { key: 'app', label: 'アプリ開発について' },
  { key: 'web', label: 'Webサービス運営について' },
  { key: 'plans', label: '料金プランについて' },
  { key: 'other', label: 'その他' },
];

const initialFormState: FormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
};

const initialChecks = Object.fromEntries(inquiryOptions.map((option) => [option.key, false])) as Record<string, boolean>;

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [checks, setChecks] = useState<Record<string, boolean>>(initialChecks);
  const [agreed, setAgreed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

  const selectedInquiryLabels = inquiryOptions
    .filter((option) => checks[option.key])
    .map((option) => option.label);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCheck = (key: string) => {
    setChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      alert('必須項目を入力してください。');
      return false;
    }

    if (selectedInquiryLabels.length === 0) {
      alert('お問い合わせ種別を1つ以上選択してください。');
      return false;
    }

    if (!agreed) {
      alert('プライバシーポリシーへの同意が必要です。');
      return false;
    }

    return true;
  };

  const handleConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setSending(true);

    const params = new URLSearchParams();
    params.set('name', form.name.trim());
    params.set('email', form.email.trim());
    params.set('phone', form.phone.trim());
    params.set('company', form.company.trim());
    params.set('inquiry_type', selectedInquiryLabels.join('、'));
    params.set('message', form.message.trim());
    params.set('privacy', agreed ? '同意済み' : '未同意');

    try {
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = (await response.text()).trim();

      if (!response.ok || text !== 'OK') {
        throw new Error('send_failed');
      }

      setForm(initialFormState);
      setChecks(initialChecks);
      setAgreed(false);
      setResult({
        status: 'success',
        message: 'お問い合わせを受け付けました。内容を確認の上、担当者よりご連絡いたします。',
      });
    } catch {
      setResult({
        status: 'error',
        message: '送信に失敗しました。時間をおいて再度お試しください。',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-900 to-cyan-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20turquoise%20ocean%20wave%20pattern%20digital%20technology%20background%20minimal%20clean%20teal%20cyan%20gradient%20with%20geometric%20shapes&width=1920&height=400&seq=ocean-contact-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-teal-200 text-xs font-semibold uppercase tracking-widest mb-3">Contact</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            お問い合わせ
          </h1>
          <p className="text-white/70 text-sm">お気軽にご相談・お問い合わせください。</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          <form onSubmit={handleConfirm} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={form.name}
                onChange={handleInputChange}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                placeholder="山田 太郎"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleInputChange}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                placeholder="example@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">電話番号</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleInputChange}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                placeholder="080-0000-0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">会社名</label>
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={handleInputChange}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                placeholder="株式会社〇〇"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                お問い合わせ種別 <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {inquiryOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleCheck(item.key)}
                    className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm text-left transition-colors ${
                      checks[item.key]
                        ? 'border-teal-400 bg-teal-50 text-teal-700'
                        : 'border-gray-200 text-gray-700 hover:border-teal-300'
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                        checks[item.key] ? 'border-teal-500 bg-teal-500' : 'border-gray-300'
                      }`}
                    >
                      {checks[item.key] && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                value={form.message}
                onChange={handleInputChange}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors resize-none"
                placeholder="ご相談内容をご記入ください"
              />
              <p className={`text-xs mt-1 text-right ${form.message.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                {form.message.length}/500
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-500 focus:ring-teal-400"
              />
              <span className="text-sm text-gray-600">
                <Link to="/privacy" className="text-teal-600 underline hover:text-teal-700">
                  プライバシーポリシー
                </Link>
                に同意する
              </span>
            </label>
            <button
              type="submit"
              disabled={sending}
              className="w-full py-3 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              入力内容を確認する
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50">
                <i className="ri-mail-line text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">メール</p>
                <a href="mailto:banking@oceansubstitute.com" className="text-sm text-teal-600 hover:underline cursor-pointer">
                  banking@oceansubstitute.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50">
                <i className="ri-phone-line text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">電話</p>
                <a href="tel:08044800273" className="text-sm text-teal-600 hover:underline cursor-pointer">
                  080-4480-0273
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-teal-100 p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <p className="text-xs font-semibold tracking-widest text-teal-500 uppercase mb-2">Confirm</p>
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  入力内容の確認
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="w-10 h-10 rounded-full border border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
                aria-label="閉じる"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4 text-sm">
              {[
                { label: 'お名前', value: form.name },
                { label: 'メールアドレス', value: form.email },
                { label: '電話番号', value: form.phone || '未入力' },
                { label: '会社名', value: form.company || '未入力' },
                { label: 'お問い合わせ種別', value: selectedInquiryLabels.join('、') },
                { label: 'お問い合わせ内容', value: form.message },
              ].map((item) => (
                <div key={item.label} className="border border-gray-100 rounded-xl p-4">
                  <p className="text-xs font-semibold tracking-wide text-gray-400 mb-2">{item.label}</p>
                  <p className="text-gray-700 whitespace-pre-line break-words">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 border border-gray-200 text-gray-700 font-semibold py-3 rounded-full hover:bg-gray-50 transition-colors"
              >
                戻って修正する
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-semibold py-3 rounded-full hover:opacity-90 transition-opacity"
              >
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {sending && (
        <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-teal-100 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-5">
              <i className="ri-loader-4-line animate-spin text-2xl text-teal-500"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              送信中です
            </h2>
            <p className="text-sm text-gray-500">内容を送信しています。しばらくお待ちください。</p>
          </div>
        </div>
      )}

      {result && (
        <div className="fixed inset-0 z-[90] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-teal-100 p-8 text-center">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 ${
                result.status === 'success' ? 'bg-emerald-50' : 'bg-red-50'
              }`}
            >
              <i
                className={`text-2xl ${
                  result.status === 'success' ? 'ri-check-line text-emerald-500' : 'ri-close-line text-red-500'
                }`}
              ></i>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {result.status === 'success' ? '送信完了しました' : '送信に失敗しました'}
            </h2>
            <p className="text-sm text-gray-500 mb-6 whitespace-pre-line">{result.message}</p>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="w-full py-3 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
